import React, { useCallback, useEffect, useState } from 'react';
import Pagination from './Pagination';
import ScmAssigneePicker from './ScmAssigneePicker';
import { useAuth } from '../context/AuthContext';
import {
  listAdminLeads,
  listAdminEvents,
  listAdminAnnouncements,
  updateLeadStatus,
  assignLead,
  getRetiredLeadCount,
  purgeRetiredLeads,
} from '../api/leadsApi';

// ── Shared styles (mirrors GalleryManagement's local style consts) ─────────
const input = { padding: '9px 11px', border: '1.5px solid #E5E7EB', borderRadius: 7, fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', color: '#111827', background: '#fff' };
const th = { textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.02em', borderBottom: '1.5px solid #E5E7EB' };
const td = { padding: '10px 12px', fontSize: 13, color: '#374151', borderBottom: '1px solid #F3F4F6', verticalAlign: 'top' };
const link = { color: '#F29213', textDecoration: 'none' };

/** The planning windows the whole feature exists to answer: "who do I need to reach this
 *  week / month / quarter".
 *
 *  GCC announcements run the OTHER WAY — the window looks backward, because there is no
 *  scheduled date, only "this was reported N days ago". Same control, opposite direction,
 *  so the labels are swapped rather than shared. Showing "Next 30 days" over a list of
 *  past announcements would be actively misleading. */
const WINDOWS_FORWARD = [
  { label: 'Next 7 days', value: 7 },
  { label: 'Next 30 days', value: 30 },
  { label: 'Next 90 days', value: 90 },
  { label: 'Next year', value: 365 },
  { label: 'All upcoming', value: '' },
];

const WINDOWS_BACKWARD = [
  { label: 'Announced in last 7 days', value: 7 },
  { label: 'Announced in last 30 days', value: 30 },
  { label: 'Announced in last 90 days', value: 90 },
  { label: 'Announced in last year', value: 365 },
  { label: 'All announcements', value: '' },
];

/** Same backward direction, but for events that have already happened rather than news. */
const WINDOWS_PAST_EVENTS = [
  { label: 'Ended in last 7 days', value: 7 },
  { label: 'Ended in last 30 days', value: 30 },
  { label: 'Ended in last 90 days', value: 90 },
  { label: 'Ended in last year', value: 365 },
];

/** Companies tab only. Past leads are not deleted, just filtered out of the default view —
 *  this is how you get back to one whose event has gone by. */
const TIMEFRAMES = [
  { label: 'Upcoming events', value: 'upcoming' },
  { label: 'Past events', value: 'past' },
];

/** eYantra's office cities first — those are the ones a rep can most easily service. */
const CITIES = ['Hyderabad', 'Noida', 'Mumbai', 'Bangalore', 'New Delhi', 'Chennai', 'Pune', 'Ahmedabad'];

const STATUS_OPTIONS = ['NEW', 'CONTACTED', 'QUALIFIED', 'DISMISSED'];

const SIGNAL_LABELS = { ORGANIZER: 'Event organiser', EXHIBITOR: 'Exhibitor', GCC_LAUNCH: 'New GCC' };
const SIGNAL_COLORS = {
  ORGANIZER: { bg: '#EFF6FF', color: '#1D4ED8' },
  EXHIBITOR: { bg: '#DCFCE7', color: '#15803D' },
  GCC_LAUNCH: { bg: '#F3E8FF', color: '#7E22CE' },
};

const SignalBadge = ({ signalType }) => {
  const c = SIGNAL_COLORS[signalType] || { bg: '#F5F5F5', color: '#555' };
  return (
    <span style={{ background: c.bg, color: c.color, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap' }}>
      {SIGNAL_LABELS[signalType] || signalType}
    </span>
  );
};

/** Urgency is the point of this screen, so it's colour-coded rather than left as a raw
 *  date the reader has to mentally subtract from today. */
const UrgencyBadge = ({ days }) => {
  if (days === null || days === undefined) return <span style={{ color: '#9CA3AF' }}>—</span>;
  let c;
  if (days < 0) c = { bg: '#F3F4F6', color: '#6B7280', text: 'passed' };
  else if (days <= 14) c = { bg: '#FEE2E2', color: '#B91C1C', text: days === 0 ? 'today' : `${days}d` };
  else if (days <= 45) c = { bg: '#FEF3C7', color: '#B45309', text: `${days}d` };
  else c = { bg: '#F3F4F6', color: '#4B5563', text: `${days}d` };
  return (
    <span style={{ background: c.bg, color: c.color, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap' }}>
      {c.text}
    </span>
  );
};

/** Deliberately NOT styled like UrgencyBadge. An announcement has no deadline, so it must
 *  never render as a red countdown — that would imply a date nobody published. Neutral
 *  grey, and the wording always says "ago" so the direction is unmistakable. */
const RecencyBadge = ({ days }) => {
  if (days === null || days === undefined) return <span style={{ color: '#9CA3AF' }}>—</span>;
  let text;
  if (days <= 0) text = 'today';
  else if (days === 1) text = 'yesterday';
  else if (days < 7) text = `${days}d ago`;
  else if (days < 60) text = `${Math.round(days / 7)}w ago`;
  else text = `${Math.round(days / 30)}mo ago`;
  return (
    <span style={{ background: '#F3F4F6', color: '#4B5563', fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap' }}>
      {text}
    </span>
  );
};

/** Ownership filter. "Mine" is the one a rep actually lives in day to day; "Unassigned" is
 *  the queue of work nobody has picked up yet. */
const OWNERSHIP = [
  { label: 'Anyone', value: '' },
  { label: 'Unassigned', value: 'unassigned' },
  { label: 'Assigned to me', value: 'mine' },
];

/** The JWT `sub` is the login identity, which on this platform is an email address. Guard
 *  anyway — if it somehow isn't one, "claim for me" is hidden rather than writing a
 *  non-address into the assignee field. */
const emailOf = (user) => {
  const candidate = user?.username;
  return typeof candidate === 'string' && candidate.includes('@') ? candidate : null;
};

/** Who owns this lead, and a way to change it.
 *
 *  Assignment is intentionally not a lock — anyone can reassign anyone. This is a small
 *  team where handing a lead over (leave, escalation) is routine, and a hard lock would
 *  just mean pinging an admin. The point is visibility: knowing at a glance that a
 *  colleague is already on a company, so two people don't call it the same morning. */
function AssigneeCell({ lead, myEmail, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const apply = async (email, graphUser) => {
    setBusy(true);
    try {
      await assignLead(lead.id, email, graphUser?.displayName);
      setEditing(false);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div style={{ minWidth: 190 }}>
        <ScmAssigneePicker
          value={lead.assignedToEmail || ''}
          onChange={(email, graphUser) => { if (graphUser) apply(email, graphUser); }}
          placeholder="Search name or email"
          disabled={busy}
        />
        <button
          onClick={() => setEditing(false)}
          style={{ marginTop: 4, background: 'none', border: 'none', padding: 0, color: '#9CA3AF', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}
        >
          cancel
        </button>
      </div>
    );
  }

  if (!lead.assignedToEmail) {
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', whiteSpace: 'nowrap' }}>
        {myEmail && (
          <button
            onClick={() => apply(myEmail, { displayName: null })}
            disabled={busy}
            style={{ padding: '4px 10px', fontSize: 11, fontWeight: 700, fontFamily: 'inherit', border: '1px solid #F29213', borderRadius: 6, background: '#fff', color: '#B45309', cursor: busy ? 'default' : 'pointer' }}
          >
            {busy ? '…' : 'Claim'}
          </button>
        )}
        <button
          onClick={() => setEditing(true)}
          style={{ background: 'none', border: 'none', padding: 0, color: '#9CA3AF', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' }}
        >
          assign…
        </button>
      </div>
    );
  }

  const isMine = myEmail && lead.assignedToEmail.toLowerCase() === myEmail.toLowerCase();
  return (
    <div style={{ minWidth: 140 }}>
      <div style={{ fontWeight: 600, color: isMine ? '#B45309' : '#374151', fontSize: 12 }}>
        {lead.assignedToName || lead.assignedToEmail}
        {isMine && <span style={{ fontWeight: 700, color: '#B45309' }}> (you)</span>}
      </div>
      {lead.assignedToName && (
        <div style={{ color: '#9CA3AF', fontSize: 11 }}>{lead.assignedToEmail}</div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 3 }}>
        <button
          onClick={() => setEditing(true)}
          style={{ background: 'none', border: 'none', padding: 0, color: '#9CA3AF', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' }}
        >
          change
        </button>
        <button
          onClick={() => apply('', null)}
          disabled={busy}
          style={{ background: 'none', border: 'none', padding: 0, color: '#9CA3AF', fontSize: 11, cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit', textDecoration: 'underline' }}
        >
          release
        </button>
      </div>
    </div>
  );
}

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

const fmtRange = (start, end) => (end && end !== start ? `${fmtDate(start)} – ${fmtDate(end)}` : fmtDate(start));

const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    style={{
      padding: '9px 16px',
      fontSize: 14,
      fontWeight: 700,
      fontFamily: 'inherit',
      border: 'none',
      borderBottom: active ? '2.5px solid #F29213' : '2.5px solid transparent',
      background: 'none',
      color: active ? '#111827' : '#9CA3AF',
      cursor: 'pointer',
    }}
  >
    {children}
  </button>
);

/** Two views over the same pipeline, because they answer different questions:
 *   • Companies — who to call, ordered by deadline. The outreach list.
 *   • Events    — what's happening and where, including shows eYantra might exhibit at
 *                 itself (gifting expos, where the exhibitors are competitors rather
 *                 than customers, so they yield no company leads). */
export default function LeadsManagement() {
  const { user } = useAuth();
  const myEmail = emailOf(user);

  const [tab, setTab] = useState('companies');
  const [withinDays, setWithinDays] = useState(30);
  const [city, setCity] = useState('');
  const [ownership, setOwnership] = useState('');
  const [timeframe, setTimeframe] = useState('upcoming');

  // Two of these three views are dated backward, so the window control has to mean the
  // opposite thing in each — and say so, rather than showing "Next 30 days" above a list
  // of things that already happened.
  const isGcc = tab === 'gcc';
  const isPast = tab === 'companies' && timeframe === 'past';
  let windows = WINDOWS_FORWARD;
  if (isGcc) windows = WINDOWS_BACKWARD;
  else if (isPast) windows = WINDOWS_PAST_EVENTS;

  // "All upcoming" (value '') has no past equivalent — an unbounded past query would drag
  // in every stale lead ever scraped — so snap to a year when switching into the past view.
  const effectiveWithinDays = isPast && withinDays === '' ? 365 : withinDays;

  return (
    <div style={{ padding: '24px 0' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: '0 0 4px', letterSpacing: '-0.5px' }}>
          Lead intelligence
        </h1>
        <p style={{ fontSize: 13, color: '#9CA3AF', margin: 0 }}>
          Upcoming India trade shows, the companies behind them, and newly announced Global Capability Centres.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: '1.5px solid #E5E7EB', marginBottom: 18, flexWrap: 'wrap' }}>
        <TabButton active={tab === 'companies'} onClick={() => setTab('companies')}>Companies to contact</TabButton>
        <TabButton active={tab === 'events'} onClick={() => setTab('events')}>Upcoming events</TabButton>
        <TabButton active={tab === 'gcc'} onClick={() => setTab('gcc')}>New GCCs</TabButton>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        {tab === 'companies' && (
          <select style={input} value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
            {TIMEFRAMES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        )}
        <select style={input} value={effectiveWithinDays} onChange={(e) => setWithinDays(e.target.value === '' ? '' : Number(e.target.value))}>
          {windows.map((w) => <option key={String(w.value)} value={w.value}>{w.label}</option>)}
        </select>
        <select style={input} value={city} onChange={(e) => setCity(e.target.value)}>
          <option value="">All cities</option>
          {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {/* Ownership is meaningless on the Events tab — events aren't assigned, companies are. */}
        {tab !== 'events' && (
          <select style={input} value={ownership} onChange={(e) => setOwnership(e.target.value)}>
            {OWNERSHIP.filter((o) => o.value !== 'mine' || myEmail).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        )}
      </div>

      {tab === 'companies' && (
        <CompaniesTab
          withinDays={effectiveWithinDays}
          city={city}
          ownership={ownership}
          myEmail={myEmail}
          past={isPast}
        />
      )}
      {tab === 'events' && <EventsTab withinDays={withinDays} city={city} />}
      {tab === 'gcc' && <GccTab withinDays={withinDays} city={city} ownership={ownership} myEmail={myEmail} />}
    </div>
  );
}

function CompaniesTab({ withinDays, city, ownership, myEmail, past }) {
  const [leads, setLeads] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');

  const unassignedOnly = ownership === 'unassigned';
  const assignee = ownership === 'mine' ? myEmail : undefined;

  // Reset to the first page whenever a filter changes — otherwise a narrowed result set
  // can leave you stranded on a page that no longer exists.
  useEffect(() => { setPage(0); }, [withinDays, city, status, ownership, past]);

  const load = useCallback(() => {
    setLoading(true);
    listAdminLeads(page, 20, { status, withinDays, city, past, unassignedOnly, assignee })
      .then((r) => {
        setLeads(r.data.content || []);
        setTotalPages(r.data.totalPages || 0);
        setTotal(r.data.totalElements || 0);
      })
      .catch(() => { setLeads([]); setTotalPages(0); setTotal(0); })
      .finally(() => setLoading(false));
  }, [page, status, withinDays, city, past, unassignedOnly, assignee]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (lead, next) => {
    await updateLeadStatus(lead.id, next);
    load();
  };

  return (
    <>
      {past && (
        <div style={{ padding: '11px 14px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13, color: '#4B5563', marginBottom: 16, lineHeight: 1.55 }}>
          <strong>Showing events that have already happened</strong>, most recent first. These leads were never
          deleted — they just drop out of the default view once their event passes. Useful for picking up a
          conversation you'd already started.
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <select style={input} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {!loading && <span style={{ fontSize: 13, color: '#9CA3AF' }}>{total} compan{total === 1 ? 'y' : 'ies'}</span>}
      </div>

      {loading ? (
        <p style={{ color: '#9CA3AF' }}>Loading…</p>
      ) : leads.length === 0 ? (
        <EmptyState
          title={past ? 'No past events in this window.' : 'No companies in this window.'}
          hint="Try widening the date range, or clearing the city filter."
        />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Company</th>
                <th style={th}>Type</th>
                <th style={th}>Contact</th>
                <th style={th}>Why / when</th>
                <th style={th}>City</th>
                <th style={th}>Event date</th>
                <th style={th}>In</th>
                <th style={th}>Status</th>
                <th style={th}>Assigned to</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.id}>
                  <td style={{ ...td, fontWeight: 600, color: '#111827', minWidth: 150 }}>
                    {lead.companyName}
                    <div style={{ marginTop: 3 }}>
                      <a href={lead.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ ...link, fontSize: 11 }}>source ↗</a>
                    </div>
                  </td>
                  <td style={td}><SignalBadge signalType={lead.signalType} /></td>
                  <td style={{ ...td, fontSize: 12, whiteSpace: 'nowrap' }}>
                    {lead.contactEmail
                      ? <div><a href={`mailto:${lead.contactEmail}`} style={link}>{lead.contactEmail}</a></div>
                      : null}
                    {lead.contactPhone ? <div style={{ color: '#6B7280' }}>{lead.contactPhone}</div> : null}
                    {!lead.contactEmail && !lead.contactPhone ? <span style={{ color: '#9CA3AF' }}>—</span> : null}
                  </td>
                  {/* Detail is newline-separated (an organiser's several shows), so preserve them. */}
                  <td style={{ ...td, maxWidth: 340, whiteSpace: 'pre-line', fontSize: 12 }}>{lead.signalDetail}</td>
                  <td style={td}>{lead.location || '—'}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(lead.eventDate)}</td>
                  <td style={td}><UrgencyBadge days={lead.daysUntilEvent} /></td>
                  <td style={td}>
                    <select
                      style={{ ...input, padding: '5px 8px', fontSize: 12 }}
                      value={lead.status}
                      onChange={(e) => handleStatusChange(lead, e.target.value)}
                    >
                      {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td style={td}>
                    <AssigneeCell lead={lead} myEmail={myEmail} onChanged={load} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
      <RetiredLeadsNotice />
    </>
  );
}

function EventsTab({ withinDays, city }) {
  const [events, setEvents] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => { setPage(0); }, [withinDays, city]);

  useEffect(() => {
    setLoading(true);
    listAdminEvents(page, 20, { withinDays, city })
      .then((r) => {
        setEvents(r.data.content || []);
        setTotalPages(r.data.totalPages || 0);
        setTotal(r.data.totalElements || 0);
      })
      .catch(() => { setEvents([]); setTotalPages(0); setTotal(0); })
      .finally(() => setLoading(false));
  }, [page, withinDays, city]);

  return (
    <>
      {!loading && <div style={{ fontSize: 13, color: '#9CA3AF', marginBottom: 12 }}>{total} event{total === 1 ? '' : 's'}</div>}

      {loading ? (
        <p style={{ color: '#9CA3AF' }}>Loading…</p>
      ) : events.length === 0 ? (
        <EmptyState title="No events in this window." hint="Try widening the date range, or clearing the city filter." />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Event</th>
                <th style={th}>Dates</th>
                <th style={th}>In</th>
                <th style={th}>City</th>
                <th style={th}>Venue</th>
                <th style={th}>Organiser</th>
                <th style={th}>Sector</th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev) => (
                <tr key={ev.id}>
                  <td style={{ ...td, fontWeight: 600, color: '#111827', minWidth: 190 }}>
                    {ev.name}
                    {/* Gifting expos are inverted for eYantra: exhibitors there are
                        competitors, and the buyers are visitors whose lists are never
                        public. Worth attending/exhibiting at, not worth mining for leads. */}
                    {ev.giftingSector && (
                      <div style={{ marginTop: 4 }}>
                        <span style={{ background: '#FEF3E2', color: '#B15400', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999 }}>
                          eYantra could exhibit
                        </span>
                      </div>
                    )}
                    <div style={{ marginTop: 3, display: 'flex', gap: 10 }}>
                      <a href={ev.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ ...link, fontSize: 11 }}>listing ↗</a>
                      {ev.officialUrl && (
                        <a href={ev.officialUrl} target="_blank" rel="noopener noreferrer" style={{ ...link, fontSize: 11 }}>site ↗</a>
                      )}
                    </div>
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtRange(ev.startDate, ev.endDate)}</td>
                  <td style={td}><UrgencyBadge days={ev.daysUntil} /></td>
                  <td style={td}>{ev.city || '—'}</td>
                  <td style={{ ...td, fontSize: 12 }}>{ev.venue || '—'}</td>
                  <td style={{ ...td, fontSize: 12 }}>
                    {ev.organizerName || '—'}
                    {ev.audience && <div style={{ color: '#9CA3AF', fontSize: 11, marginTop: 2 }}>{ev.audience}</div>}
                  </td>
                  <td style={{ ...td, maxWidth: 230, fontSize: 12, color: '#6B7280' }}>{ev.sector || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
    </>
  );
}

/** Newly announced Global Capability Centres.
 *
 *  Kept structurally separate from CompaniesTab on purpose. These leads have NO scheduled
 *  date — only the date the announcement was published — so everything that implies a
 *  deadline is deliberately absent here: the column is "Announced" not "Event date", the
 *  badge reads "3w ago" in neutral grey rather than a red countdown, and the window filter
 *  is relabelled to look backward. A rep should never be able to read a projected opening
 *  date off this screen, because no such date was published and we refuse to invent one. */
function GccTab({ withinDays, city, ownership, myEmail }) {
  const [leads, setLeads] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');

  const unassignedOnly = ownership === 'unassigned';
  const assignee = ownership === 'mine' ? myEmail : undefined;

  useEffect(() => { setPage(0); }, [withinDays, city, status, ownership]);

  const load = useCallback(() => {
    setLoading(true);
    listAdminAnnouncements(page, 20, { status, withinDays, city, unassignedOnly, assignee })
      .then((r) => {
        setLeads(r.data.content || []);
        setTotalPages(r.data.totalPages || 0);
        setTotal(r.data.totalElements || 0);
      })
      .catch(() => { setLeads([]); setTotalPages(0); setTotal(0); })
      .finally(() => setLoading(false));
  }, [page, status, withinDays, city, unassignedOnly, assignee]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (lead, next) => {
    await updateLeadStatus(lead.id, next);
    load();
  };

  return (
    <>
      <div style={{ padding: '11px 14px', background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: 8, fontSize: 13, color: '#5B21B6', marginBottom: 16, lineHeight: 1.55 }}>
        <strong>These are announcements, not scheduled events.</strong> The date shown is when the news was
        published — newest first. A company that has just announced a centre is in fit-out and mass-hiring mode
        for roughly the next 6–18 months, which is the window for welcome and onboarding kits. Opening dates are
        shown <em>only</em> where the article stated one.
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <select style={input} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {!loading && <span style={{ fontSize: 13, color: '#9CA3AF' }}>{total} announcement{total === 1 ? '' : 's'}</span>}
      </div>

      {loading ? (
        <p style={{ color: '#9CA3AF' }}>Loading…</p>
      ) : leads.length === 0 ? (
        <EmptyState
          title="No GCC announcements in this window."
          hint="Try widening the range, or clearing the city filter."
        />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Company</th>
                <th style={th}>City</th>
                <th style={th}>Announced</th>
                <th style={th}>How long ago</th>
                <th style={th}>What was reported</th>
                <th style={th}>Status</th>
                <th style={th}>Assigned to</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.id}>
                  <td style={{ ...td, fontWeight: 600, color: '#111827', minWidth: 140 }}>
                    {lead.companyName}
                    <div style={{ marginTop: 4 }}><SignalBadge signalType={lead.signalType} /></div>
                    <div style={{ marginTop: 3 }}>
                      {/* articleUrl, not sourceUrl — for GCC leads sourceUrl is a synthetic
                          dedup key (`gcc:company|city`) and isn't a navigable link. */}
                      <a href={lead.articleUrl} target="_blank" rel="noopener noreferrer" style={{ ...link, fontSize: 11 }}>article ↗</a>
                    </div>
                  </td>
                  <td style={td}>{lead.location || <span style={{ color: '#9CA3AF' }}>not stated</span>}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(lead.announcedAt)}</td>
                  <td style={td}><RecencyBadge days={lead.daysSinceAnnouncement} /></td>
                  <td style={{ ...td, maxWidth: 400, whiteSpace: 'pre-line', fontSize: 12 }}>{lead.signalDetail}</td>
                  <td style={td}>
                    <select
                      style={{ ...input, padding: '5px 8px', fontSize: 12 }}
                      value={lead.status}
                      onChange={(e) => handleStatusChange(lead, e.target.value)}
                    >
                      {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td style={td}>
                    <AssigneeCell lead={lead} myEmail={myEmail} onChanged={load} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
    </>
  );
}

const EmptyState = ({ title, hint }) => (
  <div style={{ padding: '32px 0', color: '#9CA3AF' }}>
    <p style={{ margin: '0 0 4px', fontSize: 14 }}>{title}</p>
    <p style={{ margin: 0, fontSize: 13 }}>{hint}</p>
  </div>
);

/** Surfaces leftovers from the retired scrapers: hiring, R&R certifications, the old expo
 *  directory, and the undated rows from the previous GCC scraper. (GCC itself is no longer
 *  retired — it was reinstated with a real announcement date; only its dateless legacy rows
 *  are purged.) None of these carry a date, so they appear in no tab and only take up
 *  space — but deleting them is irreversible, so it stays an explicit choice rather than
 *  something a deploy did. */
function RetiredLeadsNotice() {
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    getRetiredLeadCount().then((r) => setCount(r.data.count || 0)).catch(() => setCount(0));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (!count) return null;

  const handlePurge = async () => {
    setBusy(true);
    try {
      await purgeRetiredLeads();
      refresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 24, padding: '12px 14px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, fontSize: 13, color: '#92400E' }}>
      <strong>{count}</strong> old lead{count === 1 ? '' : 's'} from the retired hiring / R&amp;R scrapers and the
      previous undated GCC scraper are still stored. They carry no date at all, so they appear in none of these
      tabs.
      <button
        onClick={handlePurge}
        disabled={busy}
        style={{ marginLeft: 10, padding: '5px 11px', fontSize: 12, fontWeight: 700, fontFamily: 'inherit', border: '1px solid #B45309', borderRadius: 6, background: '#fff', color: '#B45309', cursor: busy ? 'default' : 'pointer' }}
      >
        {busy ? 'Deleting…' : 'Delete them'}
      </button>
    </div>
  );
}
